import { useSession } from '@context/sessionProvider';
import { toast, Button, Chip, Dropdown, Label, Modal, Table } from '@heroui/react';
import DeleteTimerIcon from '@icons/deleteTimerIcon';
import EditTimerIcon from '@icons/editTimerIcon';
import { VerticalDotsIcon } from '@icons/verticalDotsIcon';
import type { GridConfig, GridResponse } from '@lib/hooks';
import type { ConfigData } from '@/types';
import { sendDataAPI } from '@lib/sendDataAPI';
import { useRouter } from 'next/router';
import { useState } from 'react';
import { KeyedMutator } from 'swr';

type TableData = GridConfig[];

type GridTableProps = {
	data: TableData;
	mutate: KeyedMutator<GridResponse>;
	isEmpty: boolean;
};

type Column = {
	name: string;
	uid: ColumnKey;
};

type ColumnKey = keyof Pick<ConfigData, 'name' | 'priority'> | 'enable_timer' | 'actions';

const columns: Column[] = [
	{ name: 'NAME', uid: 'name' },
	{ name: 'PRIORITY', uid: 'priority' },
	{ name: 'STATUS', uid: 'enable_timer' },
	{ name: 'ACTIONS', uid: 'actions' },
];

export default function GridTable({ data, mutate, isEmpty }: GridTableProps) {
	const router = useRouter();
	const { context } = useSession();
	const [isDeleting, setIsDeleting] = useState(false);
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [currentRowId, setCurrentRowId] = useState<number | null>(null);
	const handleDeleteClick = (id: number) => {
		setCurrentRowId(id);
		setIsModalOpen(true);
	};

	const handleConfirmDelete = async () => {
		if (currentRowId !== null) {
			setIsDeleting(true);
			const res = await sendDataAPI({ jwtToken: context }, `/api/config/${currentRowId}`, 'DELETE');
			if (res.ok) {
				await mutate();
				toast.success('Timer', {
					description: 'Timer deleted successfully.',
				});
			} else {
				toast.danger('Timer', {
					description: 'Something wrong',
				});
			}
			setIsDeleting(false);
			setIsModalOpen(false);
		}
	};

	const handleCancelDelete = () => {
		setIsModalOpen(false);
	};

	const renderCell = (row: GridConfig, columnKey: ColumnKey) => {
		const cellValue = columnKey === 'actions' ? null : row[columnKey];
		switch (columnKey) {
			case 'name':
				return <p className='break-words'>{String(cellValue)}</p>;
			case 'priority':
				return <p className='break-words'>{String(cellValue)}</p>;
			case 'enable_timer':
				return (
					<Chip className='capitalize' color={row.enable_timer ? 'success' : 'default'} size='sm' variant='soft'>
						{row.enable_timer ? 'Enabled' : 'Disabled'}
					</Chip>
				);
			case 'actions':
				return (
					<div className='relative flex items-center gap-2'>
						<Dropdown className='lg:hidden'>
							<Button isIconOnly aria-label='Open timer actions' size='sm' variant='tertiary' className='lg:hidden'>
								<VerticalDotsIcon className='text-gray-300 rotate-90' />
							</Button>
							<Dropdown.Popover>
								<Dropdown.Menu
									aria-label='Timer actions'
									onAction={(key) => {
										if (key === 'edit') {
											router.push({ pathname: '/edit/[tid]', query: { tid: row.id } });
											return;
										}

										if (key === 'delete') {
											handleDeleteClick(row.id);
										}
									}}
								>
									<Dropdown.Item id='edit' textValue='Edit'>
										<EditTimerIcon />
										<Label>Edit</Label>
									</Dropdown.Item>
									<Dropdown.Item id='delete' textValue='Delete' variant='danger'>
										<DeleteTimerIcon />
										<Label>Delete</Label>
									</Dropdown.Item>
								</Dropdown.Menu>
							</Dropdown.Popover>
						</Dropdown>

						<div className='hidden lg:flex gap-2'>
							<button
								type='button'
								aria-label='Edit timer'
								title='Edit timer'
								className='text-lg text-gray-400 cursor-pointer active:opacity-50'
								onClick={() => router.push({ pathname: '/edit/[tid]', query: { tid: row.id } })}
							>
								<EditTimerIcon />
							</button>
							<button
								type='button'
								aria-label='Delete timer'
								title='Delete timer'
								className='text-lg text-danger cursor-pointer active:opacity-50'
								onClick={() => handleDeleteClick(row.id)}
							>
								<DeleteTimerIcon />
							</button>
						</div>
					</div>
				);
			default:
				return null;
		}
	};

	if (isEmpty) {
		return (
			<div className='flex flex-col items-center justify-center gap-3 py-10 text-center'>
				<p className='font-lato font-bold text-base leading-6 text-customBlack'>No timers yet</p>
				<p className='font-lato font-normal text-base leading-6 text-customLabel max-w-[520px]'>
					Create your first timer.
				</p>
			</div>
		);
	}

	return (
		<div>
			<Table className='home-timers-table'>
				<Table.ScrollContainer className='overflow-visible'>
					<Table.Content aria-label='Table of your timers' className='table-fixed w-full'>
						<Table.Header>
							{columns.map((column) => (
								<Table.Column key={column.uid} id={column.uid} className='w-1/5' isRowHeader={column.uid === 'name'}>
									{column.name}
								</Table.Column>
							))}
						</Table.Header>
						<Table.Body items={data} className='mt-2.5'>
							{(item) => (
								<Table.Row id={item.id} className='h-[61px]'>
									{columns.map((column) => (
										<Table.Cell key={column.uid} className='overflow-hidden max-w-0'>
											{renderCell(item, column.uid)}
										</Table.Cell>
									))}
								</Table.Row>
							)}
						</Table.Body>
					</Table.Content>
				</Table.ScrollContainer>
			</Table>
			<Modal>
				<Modal.Backdrop isOpen={isModalOpen} onOpenChange={setIsModalOpen}>
					<Modal.Container>
						<Modal.Dialog>
							<Modal.Header className='flex flex-col gap-1'>
								<Modal.Heading>Delete this timer?</Modal.Heading>
							</Modal.Header>
							<Modal.Body>
								<p>Are you sure you want to delete this timer?</p>
							</Modal.Body>
							<Modal.Footer>
								<Button isDisabled={isDeleting} variant='secondary' onPress={handleCancelDelete}>
									Cancel
								</Button>
								<Button isPending={isDeleting} onPress={handleConfirmDelete} variant='danger'>
									{({ isPending }) => (isPending ? 'Deleting...' : 'Delete')}
								</Button>
							</Modal.Footer>
						</Modal.Dialog>
					</Modal.Container>
				</Modal.Backdrop>
			</Modal>
		</div>
	);
}
